import React, { useEffect, useState } from "react";
import { db } from "../../firebaseConfig";
import { collection, getDocs } from "firebase/firestore";

const DashboardMetrics = () => {
  const [metrics, setMetrics] = useState({
    totalUsers: 0,
    totalInvestments: 0,
    totalRevenue: 0,
    vehiclesUnderManagement: 0,
    averageROI: 0,
  });
  const [requests, setRequests] = useState([]);
  const [formsData, setFormsData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Dark theme styling
  const pageStyle = {
    backgroundColor: "#1E1E1E",
    color: "#FFFFFF",
    minHeight: "100vh",
    fontFamily: "Arial, sans-serif",
    padding: "20px",
  };

  const headingStyle = {
    textAlign: "center",
    marginBottom: "20px",
  };

  const headingAccent = {
    color: "#007bff",
    marginBottom: "10px",
  };

  const rowContainerStyle = {
    display: "flex",
    flexWrap: "wrap",
    gap: "20px",
    marginBottom: "40px",
  };

  const metricCardStyle = {
    flex: "1",
    background: "#2C2C2C",
    borderRadius: "8px",
    padding: "20px",
    textAlign: "center",
    border: "1px solid #3A3A3A",
    boxShadow: "0 2px 5px rgba(0,0,0,0.4)",
    transition: "transform 0.2s",
  };

  const metricTitleStyle = {
    fontSize: "1rem",
    margin: "0",
    marginBottom: "10px",
  };

  const metricValueStyle = {
    fontSize: "1.5rem",
    fontWeight: "bold",
    margin: 0,
  };

  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse",
    textAlign: "left",
    backgroundColor: "#2C2C2C",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 2px 5px rgba(0,0,0,0.4)",
  };

  const thStyle = {
    padding: "10px",
    backgroundColor: "#007bff",
    color: "#fff",
    borderBottom: "1px solid #444",
  };

  const tdStyle = {
    padding: "10px",
    borderBottom: "1px solid #444",
    color: "#fff",
  };

  const formsContainerStyle = {
    backgroundColor: "#2C2C2C",
    borderRadius: "10px",
    padding: "20px",
    boxShadow: "0 2px 5px rgba(0,0,0,0.4)",
  };

  // Fetch data from Firestore on component mount
  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        setLoading(true);
        setError("");

        // 1. Fetch users
        const usersSnapshot = await getDocs(collection(db, "users"));
        const totalUsers = usersSnapshot.size;

        // 2. Fetch cars
        const carsSnapshot = await getDocs(collection(db, "cars"));
        let totalInvestments = 0;
        let totalRevenue = 0;
        let roiSum = 0;
        let totalCarsWithROI = 0;

        carsSnapshot.forEach((doc) => {
          const carData = doc.data();
          totalInvestments += carData.moneyRaised || 0;
          totalRevenue += carData.totalRevenue || 0;

          if (carData.averageMonthlyROI) {
            roiSum += carData.averageMonthlyROI;
            totalCarsWithROI++;
          }
        });

        const averageROI = totalCarsWithROI
          ? (roiSum / totalCarsWithROI).toFixed(2)
          : 0;

        setMetrics({
          totalUsers,
          totalInvestments,
          totalRevenue,
          vehiclesUnderManagement: carsSnapshot.size,
          averageROI,
        });

        // 3. Fetch fleet requests
        const requestsSnapshot = await getDocs(collection(db, "fleetRequests"));
        const requestsData = requestsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRequests(requestsData);

        // 4. Fetch forms
        const formsSnapshot = await getDocs(collection(db, "forms"));
        const formsList = formsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setFormsData(formsList);
      } catch (err) {
        console.error("Error fetching dashboard metrics:", err);
        setError("Failed to load metrics or fleet requests.");
      } finally {
        setLoading(false);
      }
    };

    fetchMetrics();
  }, []);

  if (loading) {
    return (
      <div style={pageStyle}>
        <p style={{ textAlign: "center" }}>Loading dashboard metrics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={pageStyle}>
        <p style={{ color: "red", textAlign: "center" }}>{error}</p>
      </div>
    );
  }

  return (
    <div style={pageStyle}>
      <h1 style={{ ...headingStyle, ...headingAccent }}>Dashboard Metrics</h1>

      {/* Metrics Row */}
      <div style={rowContainerStyle}>
        {Object.entries(metrics).map(([key, value]) => {
          const label = key.replace(/([A-Z])/g, " $1").trim();
          return (
            <div
              key={key}
              style={metricCardStyle}
              onMouseEnter={(e) => (e.currentTarget.style.transform = "scale(1.02)")}
              onMouseLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <h2 style={metricTitleStyle}>{label}</h2>
              <p style={metricValueStyle}>
                {typeof value === "number" ? value.toLocaleString() : value}
              </p>
            </div>
          );
        })}
      </div>

      {/* Fleet Requests */}
      <section style={{ marginBottom: "40px" }}>
        <h2 style={headingAccent}>Fleet Requests</h2>
        {requests.length === 0 ? (
          <p style={{ color: "#ccc", fontStyle: "italic" }}>
            No requests available.
          </p>
        ) : (
          <table style={tableStyle}>
            <thead>
              <tr>
                <th style={thStyle}>Investor Name</th>
                <th style={thStyle}>Investment Amount</th>
                <th style={thStyle}>Preferred Car Types</th>
                <th style={thStyle}>Contact</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((request) => (
                <tr key={request.id}>
                  <td style={tdStyle}>{request.name}</td>
                  <td style={tdStyle}>
                    ${request.investmentAmount.toLocaleString()}
                  </td>
                  <td style={tdStyle}>
                    {request.carTypes || "Not specified"}
                  </td>
                  <td style={tdStyle}>
                    <a
                      href={`mailto:${request.email}`}
                      style={{ color: "#32B720", textDecoration: "underline" }}
                    >
                      {request.email}
                    </a>
                    <br />
                    {request.phone}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* Submitted Forms */}
      <section>
        <h2 style={headingAccent}>Submitted Forms</h2>
        <div style={formsContainerStyle}>
          {formsData.length > 0 ? (
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>User ID</th>
                  <th style={thStyle}>Details</th>
                  <th style={thStyle}>Pictures</th>
                </tr>
              </thead>
              <tbody>
                {formsData.map((form) => (
                  <tr key={form.id}>
                    <td style={tdStyle}>{form.userID || "N/A"}</td>
                    <td style={tdStyle}>
                      {Object.entries(form.details || {}).map(([key, value]) => (
                        <div key={key}>
                          <strong>{key.replace(/([A-Z])/g, " $1")}: </strong>
                          {value}
                        </div>
                      ))}
                    </td>
                    <td style={tdStyle}>
                      {Array.isArray(form.pictures) && form.pictures.length > 0 ? (
                        <div style={{ display: "flex", gap: "10px" }}>
                          {form.pictures.map((pic, index) => (
                            <img
                              key={index}
                              src={pic}
                              alt={`Picture ${index + 1}`}
                              style={{
                                width: "80px",
                                height: "80px",
                                objectFit: "cover",
                                borderRadius: "5px",
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        "No pictures"
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <p style={{ textAlign: "center", color: "#ccc" }}>
              No forms available.
            </p>
          )}
        </div>
      </section>
    </div>
  );
};

export default DashboardMetrics;
